import * as React from 'react';

import {
  type InteractionTrackingEvent,
  type PageViewTrackingEvent,
  type NWTEvent,
  useTracking,
} from '@xing-com/crate-communication-tracking';
import { useViewerData } from '@xing-com/hub';

import { useHostName } from './use-host-name';

export type AdobeTrackingEvent<T = Record<string, unknown>> =
  InteractionTrackingEvent<T>;

type OptionalPageviewTrackingParams = {
  PropContextDimension1?: string;
  EventNewsArticleView?: number | string;
  PropNewsProduct?: string;
  PropNewsPublisherId?: string;
  PropNewsArticleId?: string;
  PropExperiment?: string;
  PropExperimentInfo?: string;
};

type OptionalNWTTrackingParams = {
  event?: string;
  context?: string;
  element?: string;
  elementDetail?: string;
  page?: string;
  flags?: string;
};

type OverrideAdobeData = {
  trackAction?: string;
  pageName?: string;
};

export type OptionalAdobeTrackingParams = {
  PropContextDimension1?: string;
  PropContextDimension2?: string;
  PropContextDimension4?: string;
  PropContextDimension5?: string;
  PropList?: string[];
  EventNewsArticleView?: string;
  PropActionOrigin?: string;
  EventLogout?: number;
  event?: string;
};

type AdobeData = OverrideAdobeData & OptionalAdobeTrackingParams;

type UseVitaTrackingProps = OverrideAdobeData;

type UseVitaTrackingReturn = {
  trackPageView: (extraData?: OptionalPageviewTrackingParams) => void;
  trackWithNWT: (extraData?: OptionalNWTTrackingParams) => void;
  trackInteraction: (
    overrideTrackAction?: string,
    extraData?: Omit<AdobeData, 'trackAction'>
  ) => void;
  trackScrolling: (
    propScrolling: string,
    extraData?: Omit<AdobeData, 'trackAction'>
  ) => void;
  getAdobeTrackedUrl: (url: string, optionalParams?: AdobeData) => string;
};

export type AdobePropTrackActionEvent = AdobeTrackingEvent<
  {
    type: 'interaction';
    event: 'PropTrackAction' | 'PropScrolling';
    PropTrackAction: string;
  } & Record<string, unknown>
>;

export const getAdobePropTrackActionEvent = (
  trackActionProp: string,
  options?: Record<string, unknown>
): AdobePropTrackActionEvent => ({
  type: 'interaction',
  event: 'PropTrackAction',
  PropTrackAction: trackActionProp,
  ...options,
});

export const useVitaTracking = ({
  trackAction,
  pageName,
}: UseVitaTrackingProps = {}): UseVitaTrackingReturn => {
  const { track } = useTracking<
    AdobeTrackingEvent | PageViewTrackingEvent | NWTEvent
  >();
  const userId = useViewerData().data?.user?.id;
  const hostName = useHostName();
  const isXing = hostName.includes('xing');
  const isLebenslauf = hostName.includes('lebenslauf');
  const prefix = isXing ? 'wbm' : isLebenslauf ? 'leb' : 'unknown';
  const channel = prefix + '/lebenslauf';
  const page = `${channel}/${pageName}`;
  const PropActionOrigin = `lebenslauf_${pageName}_${prefix}`;

  const trackPageView = React.useCallback<
    UseVitaTrackingReturn['trackPageView']
  >(
    (extraData) => {
      track({ type: 'pageview', channel, page, ...extraData });
    },
    [track, channel, page]
  );

  const trackWithNWT = React.useCallback<UseVitaTrackingReturn['trackWithNWT']>(
    (extraData) => {
      const application = prefix;
      const page = `lebenslauf/${pageName}`;
      track({
        type: 'nwt',
        event: 'viewed_screen',
        eventSchema: 'basic',
        schemaVersion: '1.17.0',
        sentBy: 'lebenslauf',
        channel,
        application,
        page,
        userId,
        ...extraData,
      });
    },
    [channel, pageName, track, prefix, userId]
  );

  const trackInteraction = React.useCallback<
    UseVitaTrackingReturn['trackInteraction']
  >(
    (overrideTrackAction, extraData) => {
      const sentTrackAction = overrideTrackAction ?? trackAction;
      const event =
        sentTrackAction &&
        getAdobePropTrackActionEvent(sentTrackAction, { ...extraData });

      event && track(event);
    },
    [track, trackAction]
  );

  const trackScrolling = React.useCallback<
    UseVitaTrackingReturn['trackScrolling']
  >(
    (propScrolling, extraData) => {
      track({
        type: 'interaction',
        event: 'PropScrolling',
        ...(propScrolling && { PropScrolling: propScrolling }),
        ...extraData,
      });
    },
    [track]
  );

  const getAdobeTrackedUrl = React.useCallback(
    (url: string, optionalParams?: AdobeData): string => {
      const {
        trackAction: overrideTrackAction,
        PropActionOrigin: overridePropActionOrigin,
        ...optionalParamsRest
      } = optionalParams ?? {};

      const usedTrackAction = overrideTrackAction ?? trackAction;
      const usedPropActionOrigin = overridePropActionOrigin ?? PropActionOrigin;

      return usedTrackAction
        ? getAdobeTrackingUrl(url, usedTrackAction, {
            PropActionOrigin: usedPropActionOrigin,
            ...optionalParamsRest,
          })
        : url;
    },
    [trackAction, PropActionOrigin]
  );

  const getAdobeTrackingUrl = (
    originalUrl: string,
    trackAction: string,
    optionalParams?: OptionalAdobeTrackingParams
  ): string => {
    const { PropActionOrigin } = optionalParams ?? {};
    const trackingParams = [
      `sc_o=${trackAction}`,
      PropActionOrigin && `sc_o_PropActionOrigin=${PropActionOrigin}`,
    ].filter((param): param is string => !!param);
    const urlHasQueryString = originalUrl.includes('?');
    const separator = urlHasQueryString ? '&' : '?';

    return `${originalUrl}${separator}${trackingParams.join('&')}`;
  };

  return {
    trackPageView,
    trackInteraction,
    trackScrolling,
    trackWithNWT,
    getAdobeTrackedUrl,
  };
};
